// FORM: https://github.com/modusign/modusign-client/blob/1f2cc00e317b3cc6ddf42ace319c72036470a423/app/src/components/RequesterImageField/index.tsx

import { AlertModal } from '@modusign/design-system';
import { useImageField } from '@modusign/react-signing';
import { Size } from '@modusign/react-signing/types';
import { useCallback, useEffect, useState } from 'react';

import { clampFieldPoint } from '@app/features/apply-react-signing/components/FieldRenderer/libs/clampFieldPoint';
import { RequesterImageField } from '@app/features/apply-react-signing/components/FieldRenderer/Setup/components/RequesterFields/RequesterImageField';
import {
  Point,
  ReactSigningFieldRendererProps,
} from '@app/features/apply-react-signing/components/FieldRenderer/types';
import { useOptimalFieldsContext } from '@app/features/apply-react-signing/libs/hooks/useCollectionOptimal';
import useDownloadImage from '@app/hooks/documentSetup/useDownloadImage';
import useUploadDocumentImage from '@app/hooks/documentSetup/useUploadDocumentImage';
import coalesceGlobalErrorMessage from '@app/libs/coalesceGlobalErrorMessage';

function RequesterImageFieldRenderer({
  initialContextMenuOpened = true,
  isPreview = false,
  isDataLabelEditable = true,
  readOnly,
  fieldId,
  viewport,
}: ReactSigningFieldRendererProps) {
  const { field, setImage, setImageSrc, setDataLabel, setSize, setPosition } =
    useImageField(fieldId);
  const {
    evaluated: { isDataLabelDuplicated },
    removeField,
  } = useOptimalFieldsContext<'IMAGE', 'REQUESTER'>();

  const [downloadErrorMessage, setDownloadErrorMessage] = useState('');
  const [uploadErrorMessage, setUploadErrorMessage] = useState('');

  const {
    error: uploadError,
    isLoading: isUploading,
    mutate: uploadDocumentImage,
  } = useUploadDocumentImage({
    onError: (error) => {
      setUploadErrorMessage(error.message || '이미지를 업로드할 수 없습니다.');
    },
    onSuccess: (
      { fileId, otp }: { fileId: string; otp: string },
      { imageSrc }: { base64: string; imageSrc: string }
    ) => {
      setImage({ fileId, otp });
      setImageSrc(imageSrc);
    },
  });

  const {
    error: downloadError,
    isLoading: isDownloading,
    refetch: downloadImage,
  } = useDownloadImage(
    {
      url: field?.url ?? '',
    },
    {
      enabled: false,
      onSuccess: (imageSrc) => {
        if (imageSrc) {
          handleImageChange({ imageSrc });
        }
      },
      onError: (error) => {
        setDownloadErrorMessage(error.message);
      },
    }
  );

  const isError = !!uploadError || !!downloadError;
  const isLoading = isDownloading || isUploading;

  const handleImageChange = useCallback(
    ({ imageSrc }: { imageSrc: string; fileName?: string }) => {
      const base64 = imageSrc.split(',').pop();
      if (base64) {
        uploadDocumentImage({ base64, imageSrc });
      }
    },
    [uploadDocumentImage]
  );

  const handlePointChange = useCallback(
    (point: Point) => {
      const clampedPoint = clampFieldPoint(point);
      setPosition(clampedPoint.x, clampedPoint.y);
    },
    [setPosition]
  );

  const handleSizeChange = useCallback(
    (size: Size) => {
      setSize(size.width, size.height);
    },
    [setSize]
  );

  useEffect(() => {
    if (field?.url && !field.src) {
      downloadImage();
    }
  }, [downloadImage, field, field?.src]);

  if (field == null) {
    return null;
  }

  return (
    <>
      <RequesterImageField
        field={field}
        imageSrc={field?.src}
        initialContextMenuOpened={initialContextMenuOpened}
        isDataLabelDuplicated={isDataLabelDuplicated}
        isDataLabelEditable={isDataLabelEditable}
        isError={isError}
        isLoading={isLoading}
        isPreview={isPreview}
        readOnly={readOnly}
        viewport={viewport}
        onDataLabelChange={setDataLabel}
        onFieldRemove={removeField}
        onImageChange={handleImageChange}
        onPointChange={handlePointChange}
        onSizeChange={handleSizeChange}
      />
      {downloadErrorMessage && (
        <AlertModal
          isOpen={!!downloadErrorMessage}
          title="이미지 다운로드 실패"
          variant="warning"
          onClose={() => setDownloadErrorMessage('')}
        >
          {coalesceGlobalErrorMessage(downloadErrorMessage)}
        </AlertModal>
      )}
      {uploadErrorMessage && (
        <AlertModal
          isOpen={!!uploadErrorMessage}
          title="이미지 업로드 실패"
          variant="warning"
          onClose={() => setUploadErrorMessage('')}
        >
          {coalesceGlobalErrorMessage(uploadErrorMessage)}
        </AlertModal>
      )}
    </>
  );
}

export default RequesterImageFieldRenderer;
